"use client";

import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Code } from "lucide-react";
import { cn } from "@/lib/utils";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { APP_EMBEDS, appEmbedCode, type AppEmbedType, type EmbedTheme } from "@/lib/embeds/app-embeds";

type AppEmbedShareDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: AppEmbedType;
  appId: number;
  country?: string;
  /** iframe height when the section knows better than APP_EMBEDS. */
  height?: number;
};

/** The embed code dialog for an app page section (see APP_EMBEDS); same layout as RankingHistoryShareDialog. */
export default function AppEmbedShareDialog({ open, onOpenChange, type, appId, country, height }: AppEmbedShareDialogProps) {
  const [embedCopied, setEmbedCopied] = React.useState(false);
  const [embedTheme, setEmbedTheme] = React.useState<EmbedTheme>('system');
  const spec = APP_EMBEDS[type];

  // Hydration-safe terminal mode
  const { isTerminal, mounted } = useThemeConfig();
  const showTerminal = mounted && isTerminal;

  const embedCode = React.useMemo(
    () => appEmbedCode(type, appId, { country, theme: embedTheme, height }),
    [type, appId, country, embedTheme, height]
  );

  const onCopyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 1200);
    } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "sm:max-w-md p-0 overflow-hidden",
        showTerminal && "bg-black border-emerald-500/50"
      )}>
        {/* Terminal mode decorations */}
        {showTerminal && (
          <>
            {/* Scanline overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.08] bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(16,185,129,0.1)_2px,rgba(16,185,129,0.1)_4px)]" />

            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-emerald-400/80" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-emerald-400/80" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-emerald-400/80" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-emerald-400/80" />

            {/* Terminal header bar */}
            <div className="relative z-10 flex items-center gap-2 px-4 py-2 border-b border-emerald-500/30 bg-emerald-950/30">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-500/60" />
                <div className="w-2 h-2 rounded-full bg-emerald-500/40" />
                <div className="w-2 h-2 rounded-full bg-emerald-500/20" />
              </div>
              <div className="flex-1" />
              <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-emerald-500/70 [text-shadow:0_0_8px_rgba(52,211,153,0.5)]">
                SYS.EMBED
              </span>
            </div>
          </>
        )}

        <div className={cn("relative z-10", showTerminal ? "p-5 pt-3" : "p-6")}>
          <DialogHeader className={showTerminal ? "mb-4" : undefined}>
            <DialogTitle className={cn(
              "flex items-center gap-2",
              showTerminal && "font-mono text-emerald-400 [text-shadow:0_0_10px_rgba(52,211,153,0.6)]"
            )}>
              {showTerminal && <Code className="h-4 w-4 text-emerald-500 [filter:drop-shadow(0_0_4px_rgba(52,211,153,0.8))]" />}
              {showTerminal ? spec.terminalLabel : spec.label}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className={cn(
                "text-sm font-medium mb-1.5 block",
                showTerminal ? "text-emerald-500/70 font-mono text-xs uppercase tracking-wider" : "text-muted-foreground"
              )}>
                {showTerminal ? "THEME.SELECT" : "Theme"}
              </label>
              <div className="flex gap-2">
                {(['system', 'dark', 'light'] as const).map((theme) => (
                  <button
                    key={theme}
                    type="button"
                    onClick={() => setEmbedTheme(theme)}
                    className={cn(
                      "flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      showTerminal
                        ? cn(
                            "font-mono text-xs uppercase tracking-wider border",
                            embedTheme === theme
                              ? "border-emerald-500/70 bg-emerald-500/20 text-emerald-400 [text-shadow:0_0_8px_rgba(52,211,153,0.6)]"
                              : "border-emerald-500/30 text-emerald-600 hover:text-emerald-400 hover:border-emerald-500/50"
                          )
                        : cn(
                            embedTheme === theme
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted hover:bg-muted/80"
                          )
                    )}
                  >
                    {theme.charAt(0).toUpperCase() + theme.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={cn(
                "text-sm font-medium mb-1.5 block",
                showTerminal ? "text-emerald-500/70 font-mono text-xs uppercase tracking-wider" : "text-muted-foreground"
              )}>
                {showTerminal ? "OUTPUT.CODE" : "Embed Code"}
              </label>
              <div className={cn(
                "rounded-md border p-3",
                showTerminal ? "bg-emerald-950/30 border-emerald-500/30" : "bg-muted/30"
              )}>
                <pre className={cn(
                  "text-xs overflow-x-auto whitespace-pre-wrap break-all",
                  showTerminal ? "text-emerald-400/80 font-mono [text-shadow:0_0_4px_rgba(52,211,153,0.3)]" : "text-muted-foreground"
                )}>
                  {embedCode}
                </pre>
              </div>
              <Button
                size="sm"
                variant="secondary"
                className={cn(
                  "w-full mt-2",
                  showTerminal && "bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/30 hover:text-emerald-300 font-mono text-xs uppercase tracking-wider"
                )}
                onClick={onCopyEmbed}
              >
                <Copy className="h-3.5 w-3.5 mr-1" />
                {embedCopied ? "Copied!" : "Copy Embed Code"}
              </Button>
            </div>

            <p className={cn(
              "text-xs",
              showTerminal ? "text-emerald-500/50 font-mono" : "text-muted-foreground"
            )}>
              {showTerminal ? spec.terminalDescription : spec.description}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
